import Button from "../../button/Button";
import styles from "./ChangeEmailModal.module.scss";
import close from "../../../assets/icons/close.svg";
import Input from "../../input/Input";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { useEmailUpdateRequestMutation, useEmailUpdateVerifyMutation } from "../../../store/services/auth-api";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { trackEvent } from "../../../utils/analytics";
import { EVENT_NAMES } from "../../../types/event-names";

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === "object" && error !== null && "status" in error;
}
interface ChangeEmailModalProps {
  setIsModalEmailVisible: (arg: boolean) => void;
  setIsModalConfirmVisible: (arg: boolean) => void;
  setNewEmailAddress?: (arg: string) => void;
}

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({
  setIsModalEmailVisible,
  setIsModalConfirmVisible,
  setNewEmailAddress,
}) => {
  const [emailFocused, setEmailFocused] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [confirmFocused, setConfirmFocused] = useState(false);
  const [confirmValue, setConfirmValue] = useState("");
  const [emailMismatch, setEmailMismatch] = useState(false);
  const [error, setError] = useState(false);
  const [errorValue, setErrorValue] = useState("");
  const [errorCount, setErrorCount] = useState(0);
  const { t } = useTranslation();
  const [emailUpdateRequest] = useEmailUpdateRequestMutation();
  const [emailUpdateVerify] = useEmailUpdateVerifyMutation();
  const [isLoader, setIsLoader] = useState(false);

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (emailValue === "" || checkEmailIsValid(emailValue)) setErrorValue("");
      else if (!checkEmailIsValid(emailValue)) setErrorValue("Email must be legal address");
    }, 1000);

    return () => {
      clearTimeout(timerId);
    };
  }, [emailValue]);

  useEffect(() => {
    if (errorCount >= 2) {
      setError(true);
    }
  }, [errorValue, errorCount]);

  const checkEmailIsValid = (val: string) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(val);
  };

  const handleConfirmChange = (e: any) => {
    setConfirmValue(e.target.value);
    setEmailMismatch(e.target.value !== emailValue);
  };

  const handleEmailChange = (e: any) => {
    setEmailValue(e.target.value);
    if (confirmValue) {
      setEmailMismatch(e.target.value !== confirmValue);
    }
  };

  const onChangeEmailClickHandler = async () => {
    if (emailMismatch || !emailValue || !confirmValue) return;

    const payload = { email: emailValue };

    try {
      setIsLoader(true);
      trackEvent(EVENT_NAMES.CHANGE_EMAIL_VERIFICATION_SENT);

      const check = await emailUpdateVerify(payload);

      if (check.error && isFetchBaseQueryError(check.error)) {
        const { message } = check.error.data as { message: string };
        setErrorCount((prev) => prev + 2);
        setErrorValue(message);
        setIsLoader(false);
        return;
      }

      const res = await emailUpdateRequest(payload);

      if (res.error && isFetchBaseQueryError(res.error)) {
        const { result } = res.error.data as { result?: { err?: string } };
        setErrorCount((prev) => prev + 1);
        setErrorValue(result?.err || "");
        trackEvent(EVENT_NAMES.CHANGE_EMAIL_FAIL);
        setIsLoader(false);

        return;
      }

      // Success
      trackEvent(EVENT_NAMES.CHANGE_EMAIL_SUCCESS);
      setIsLoader(false);

      setNewEmailAddress?.(emailValue);
      setIsModalEmailVisible(false);
      setIsModalConfirmVisible(true);
    } catch (err) {
      console.error("Error updating email:", err);
      trackEvent(EVENT_NAMES.CHANGE_EMAIL_FAIL);
      setIsLoader(false);
    }
  };

  const confirmBorderClass = confirmValue && emailMismatch ? styles.root_main_inputs_error : "";

  useEffect(() => {
    trackEvent(EVENT_NAMES.CHANGE_EMAIL_INITIATED);
  }, []);

  return (
    <div className={cx(styles.root, error && styles.root_error)}>
      <img src={close} alt="close" onClick={() => setIsModalEmailVisible(false)} />
      <div className={styles.root_main}>
        <p className={styles.root_main_title}>{t("Update Your Email Address")}</p>
        <p className={styles.root_main_subtitle}>{t("Keep Your Fortect Account Information Up-to-Date")}</p>
        <div className={styles.root_main_inputs}>
          <Input
            type={"text"}
            value={emailValue}
            onChange={handleEmailChange}
            onFocus={() => setEmailFocused(true)}
            onBlur={() => setEmailFocused(false)}
            focused={emailFocused}
            placeholder={t("Enter new Email")}
          />
          <Input
            type={"text"}
            value={confirmValue}
            onChange={handleConfirmChange}
            onFocus={() => setConfirmFocused(true)}
            onBlur={() => setConfirmFocused(false)}
            focused={confirmFocused}
            placeholder={t("Confirm new Email")}
            className={confirmBorderClass}
            error={emailMismatch}
          />
        </div>

        <div className={styles.root_main_message}>
          {errorValue && <p className={styles.root_main_message_error}>{t(errorValue)}</p>}
          {!errorValue && confirmValue && emailMismatch ? (
            <p className={styles.root_main_message_error}>{t("Email must match.")}</p>
          ) : (
            !errorValue && confirmValue && <p className={styles.root_main_message_successful}>{t("Email match.")}</p>
          )}
        </div>
        <Button
          onClick={onChangeEmailClickHandler}
          text={t("Change Email")}
          type={!emailMismatch && emailValue && confirmValue && !errorValue ? "primary" : "disabled"}
          classNames={styles.root_main_footer}
          isLoader={isLoader}
        />
        {error && (
          <p className={styles.root_support}>
            {t("Need Help?")} <span className={styles.root_support_link}> {t("Contact support")}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default ChangeEmailModal;
